import React from "react";

export type MODAL_VIEWS =
  | "ACTION_SUCCESS"
  | "INVOICE_VIEW"
  | "UPDATE_FEE_VIEW"
  | "UPDATE_GENERAL_LIMITATIONS_VIEW"
  | "UPDATE_DAILY_LIMITATIONS_VIEW"
  | "PENDING_CONFIRM_VIEW"
  | "PENDING_DEPOSIT_AMOUNT_VIEW"
  | "ADMIN_DELETE_CONFIRM_VIEW"
  | "FEE_DELETE_CONFIRM_VIEW"
  | "ACCOUNT_DELETE_CONFIRM_VIEW"
  | "ADMIN_SUCCESS_VIEW"
  | "VERIFY_USER"
  | "ADD_IBAN_ACCOUNT"
  | "REJECT_USER"
  | "ACCEPT_UNIDENTIFY_DEPOSIT"
  | "PHOTO_2FA_VIEW"
  | "RESET_2FA_ACCEPT_VIEW"
  | "REJECT_UNIDENTIFY_DEPOSIT";

interface State {
  view: MODAL_VIEWS | undefined;
  data?: any;
  isOpen: boolean;
}
type Action =
  | { type: "open"; view: MODAL_VIEWS; payload?: any }
  | { type: "close" };

const initialState: State = {
  view: undefined,
  isOpen: false,
  data: null,
};

function modalReducer(state: State, action: Action): State {
  switch (action.type) {
    case "open":
      return {
        ...state,
        view: action.view,
        data: action.payload,
        isOpen: true,
      };
    case "close":
      return {
        ...state,
        view: undefined,
        data: null,
        isOpen: false,
      };
    default:
      throw new Error("Unknown Modal Action!");
  }
}

const ModalStateContext = React.createContext<State>(initialState);
ModalStateContext.displayName = "ModalStateContext";
const ModalActionContext = React.createContext<
  React.Dispatch<Action> | undefined
>(undefined);
ModalActionContext.displayName = "ModalActionContext";

export default function ModalProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const [state, dispatch] = React.useReducer(modalReducer, initialState);
  return (
    <ModalStateContext.Provider value={state}>
      <ModalActionContext.Provider value={dispatch}>
        {children}
      </ModalActionContext.Provider>
    </ModalStateContext.Provider>
  );
}

export function useModalState() {
  const context = React.useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error(`useModalState must be used within a ModalProvider`);
  }
  return context;
}

export function useModalAction() {
  const dispatch = React.useContext(ModalActionContext);
  if (dispatch === undefined) {
    throw new Error(`useModalAction must be used within a ModalProvider`);
  }
  return {
    openModal(view: MODAL_VIEWS, payload?: unknown) {
      dispatch({ type: "open", view, payload });
    },
    closeModal() {
      dispatch({ type: "close" });
    },
  };
}
